import React from "react";
import type { MessageContent, MessageMetadata } from "./types";
import { AlertCircle, Check, CheckCircle, Edit2, X } from "lucide-react";
import { MessageContent as MarkdownRenderer } from "./MessageContent";
import { cn } from "@/lib/utils";
import { useSystemMessageAction } from "@/hooks/useSystemMessageAction";

interface PAMessageProps {
  id: string;
  content: MessageContent;
  timestamp: string;
  metadata?: MessageMetadata;
}

export const PAMessage: React.FC<PAMessageProps> = ({ id, content, timestamp, metadata }) => {
  const { confirmLearning, dismissLearning, editLearning, viewAction, loading } = useSystemMessageAction();
  // Extract text content from structured format
  const getMessageText = (content: MessageContent): string => {
    // Handle structured content format: {"parts": [{"type": "text", "content": "..."}]}
    if (content.parts && Array.isArray(content.parts)) {
      const textParts = content.parts
        .filter((part: { type: string; content?: string }) => part.type === "text")
        .map((part: { type: string; content?: string }) => part.content || "")
        .join(" ");
      return textParts || "";
    }

    // Fallback to simple text format
    return content.text || "";
  };

  const messageText = getMessageText(content);
  const messageType = metadata?.messageType as string | undefined;

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Handler functions for system message actions
  const handleConfirm = async () => {
    try {
      await confirmLearning(id);
    } catch (error) {
      console.error("Failed to confirm learning:", error);
    }
  };

  const handleDismiss = async () => {
    try {
      await dismissLearning(id);
    } catch (error) {
      console.error("Failed to dismiss:", error);
    }
  };

  const handleEdit = async () => {
    try {
      await editLearning(id);
    } catch (error) {
      console.error("Failed to edit learning:", error);
    }
  };

  const handleAccept = async () => {
    try {
      await viewAction(id);
    } catch (error) {
      console.error("Failed to accept action:", error);
    }
  };

  // Render proactive action message
  if (messageType === "proactive_action") {
    return (
      <div className="flex w-full mb-4 justify-start">
        <div className="flex items-start space-x-3 max-w-[70%]">
          {/* PA Avatar */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <span className="text-white text-sm font-medium">PA</span>
          </div>

          {/* Proactive Action Content */}
          <div
            className={cn(
              "rounded-lg px-4 py-3 shadow-sm",
              "bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800",
            )}
            role="region"
            aria-label="Proactive action suggestion from Personal Assistant"
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <div className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Suggested Action
              </div>
            </div>

            <div className="text-blue-700 dark:text-blue-300 mb-3">
              <MarkdownRenderer content={messageText} />
            </div>

            <div className="flex gap-2" role="group" aria-label="Proactive action options">
              <button 
                type="button"
                onClick={handleAccept}
                disabled={loading}
                className="px-3 py-1 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Accept suggested action"
              >
                <Check className="w-3 h-3" aria-hidden="true" />
                Accept
              </button>
              <button 
                type="button"
                onClick={handleDismiss}
                disabled={loading}
                className="px-3 py-1 text-xs font-medium rounded-md bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Dismiss suggested action"
              >
                <X className="w-3 h-3" aria-hidden="true" />
                Dismiss
              </button>
            </div>

            <div className="text-xs opacity-70 mt-2">{formatTimestamp(timestamp)}</div>
          </div>
        </div>
      </div>
    );
  }

  // Render learning confirmation message
  if (messageType === "learning_confirmation") {
    return (
      <div className="flex w-full mb-4 justify-start">
        <div className="flex items-start space-x-3 max-w-[70%]">
          {/* PA Avatar */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <span className="text-white text-sm font-medium">PA</span>
          </div>

          {/* Learning Confirmation Content */}
          <div
            className={cn(
              "rounded-lg px-4 py-3 shadow-sm",
              "bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800",
            )}
            role="region"
            aria-label="Learning confirmation from Personal Assistant"
          >
            <div className="flex items-center gap-2 mb-2">
              <Edit2 className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
              <div className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Learning Update
              </div>
            </div>

            <div className="text-yellow-700 dark:text-yellow-300 mb-3">
              <MarkdownRenderer content={messageText} />
            </div>

            <div className="flex gap-2" role="group" aria-label="Learning confirmation options">
              <button 
                type="button"
                onClick={handleConfirm}
                disabled={loading}
                className="px-3 py-1 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Confirm this learning"
              >
                <CheckCircle className="w-3 h-3" aria-hidden="true" />
                Confirm
              </button>
              <button 
                type="button"
                onClick={handleEdit}
                disabled={loading}
                className="px-3 py-1 text-xs font-medium rounded-md bg-yellow-600 text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Correct this learning"
              >
                <Edit2 className="w-3 h-3" aria-hidden="true" />
                Correct
              </button>
              <button 
                type="button"
                onClick={handleDismiss}
                disabled={loading}
                className="px-3 py-1 text-xs font-medium rounded-md bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Dismiss this learning"
              >
                <X className="w-3 h-3" aria-hidden="true" />
                Dismiss
              </button>
            </div>

            <div className="text-xs opacity-70 mt-2">{formatTimestamp(timestamp)}</div>
          </div>
        </div>
      </div>
    );
  }

  // Render system notification message
  if (messageType === "system_notification") {
    return (
      <div className="flex w-full mb-4 justify-start">
        <div className="flex items-start space-x-3 max-w-[70%]">
          {/* PA Avatar */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <span className="text-white text-sm font-medium">PA</span>
          </div>

          {/* System Notification Content */}
          <div
            className={cn(
              "rounded-lg px-4 py-3 shadow-sm",
              "bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700",
            )}
            role="status"
            aria-label="System notification from Personal Assistant"
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                System Update
              </div>
            </div>

            <div className="text-gray-700 dark:text-gray-300">
              <MarkdownRenderer content={messageText} />
            </div>

            <div className="text-xs opacity-70 mt-2">{formatTimestamp(timestamp)}</div>
          </div>
        </div>
      </div>
    );
  }

  // Render objective update message
  if (messageType === "objective_update") {
    return (
      <div className="flex w-full mb-4 justify-start">
        <div className="flex items-start space-x-3 max-w-[70%]">
          {/* PA Avatar */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <span className="text-white text-sm font-medium">PA</span>
          </div>

          {/* Objective Update Content */}
          <div
            className={cn(
              "rounded-lg px-4 py-3 shadow-sm",
              "bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800",
            )}
            role="status"
            aria-label="Objective update from Personal Assistant"
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <div className="text-sm font-medium text-purple-800 dark:text-purple-200">
                Objective Updated
              </div>
            </div>

            <div className="text-purple-700 dark:text-purple-300">
              <MarkdownRenderer content={messageText} />
            </div>

            <div className="text-xs opacity-70 mt-2">{formatTimestamp(timestamp)}</div>
          </div>
        </div>
      </div>
    );
  }

  // Default rendering for regular messages
  return (
    <div className="flex w-full mb-4 justify-start">
      <div className="flex items-start space-x-3 max-w-[70%]">
        {/* PA Avatar */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
          <span className="text-white text-sm font-medium">PA</span>
        </div>

        {/* Message Content */}
        <div
          className={cn(
            "rounded-lg px-4 py-2 shadow-sm",
            "bg-muted text-muted-foreground border border-border",
          )}
        >
          <div className="text-sm font-medium mb-1 text-blue-600">Personal Assistant</div>

          <MarkdownRenderer content={messageText} />

          <div className="text-xs opacity-70 mt-2">{formatTimestamp(timestamp)}</div>
        </div>
      </div>
    </div>
  );
};
